import { FC, useContext, useEffect, useState } from 'react';

import {
  Button,
  Input,
  ISelectOption,
  SearchIcon,
  Select,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import { ClientRegisterModal } from 'modals';
import s from 'pages/Clients/Clients.module.scss';
import { IClientsRequest } from 'schemas';
import { SettingsContext } from 'settings';
import { formatSum } from 'utils';

import { ClientsTableTopProps } from '../interfaces';
import { ExportUsers } from './ExportUsers';
import { GroupControls } from './GroupControls';

export const Top: FC<ClientsTableTopProps> = ({
  OpenTabRadio,
  groups,
  variables,
  getDebouncedClients,
  selectedClientsUUIDs,
  setSelectedClientsUUIDs,
  data,
  getClients,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    state: {
      settings: {
        clientRegister,
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const [groupSelectValue, setGroupSelectValue] = useState<string | null>(null);
  const [isClientRegisterOpen, setIsClientRegisterOpen] = useState(false);

  const groupsSelectOptions: ISelectOption[] =
    groups?.map(({ uuid, title }) => ({
      value: uuid,
      title,
    })) || [];

  const [clientName, setClientName] = useState('');

  useEffect(() => {
    const length = clientName.length;
    if (length !== 0 && length < 2) {
      return;
    }

    const input: IClientsRequest['input'] = {
      sort: variables?.input?.sort,
      q: length === 0 ? '' : clientName,
    };

    if (groupSelectValue) {
      input.filter = { groupUUID: groupSelectValue };
    }

    getDebouncedClients({
      variables: {
        ...variables,
        page: 1,
        input,
      },
    });
  }, [clientName, getDebouncedClients, groupSelectValue]);

  const total_deposits = data?.clients?.total_deposits;

  const totalDepositsFormatted =
    typeof total_deposits === 'number'
      ? formatSum(total_deposits, currency, { maximumFractionDigits: 2 })
      : 0;

  const selectedClientsLength = selectedClientsUUIDs.length;

  return (
    <>
      {isClientRegisterOpen && (
        <ClientRegisterModal
          variables={variables}
          getClients={getClients}
          onClose={() => setIsClientRegisterOpen(false)}
        />
      )}
      <div className={s.top}>
        {permissions?.has('client_browse') && OpenTabRadio}
        <div className={s.controllers}>
          <Input
            value={clientName}
            onChange={setClientName}
            leftContent={<SearchIcon color="primary" size="m" />}
            placeholder={t('clients.search.placeholder')}
            className={s['search']}
            variant="secondary"
            errorWrapperClassName={s.errorWrapper}
          />
          {permissions?.has('client_manage') && groupsSelectOptions.length > 0 && (
            <Select
              name="groups"
              options={groupsSelectOptions}
              value={groupSelectValue}
              onChange={(event) => {
                setGroupSelectValue(event);
              }}
              placeholder={t('groups.all')}
              errorWrapperClassName={cn(s.errorWrapper, s.groupSelect)}
            />
          )}
        </div>
        <div className={cn(s.deposits, s.secondary)}>
          {permissions?.has('client_manage') && (
            <>
              <div className={s.depositsText}>
                {t('clients.total.deposits')}:{' '}
              </div>
              <div className={s.depositsCount}>{totalDepositsFormatted}</div>
            </>
          )}
        </div>
        <div
          className={cn(
            s.buttons,
            selectedClientsLength > 0 && s.buttons_double
          )}
        >
          {permissions?.has('client_manage') && selectedClientsLength > 0 && (
            <GroupControls
              groups={groups}
              selectedClientsUUIDs={selectedClientsUUIDs}
              setSelectedClientsUUIDs={setSelectedClientsUUIDs}
              data={data}
            />
          )}
          <div className={s.buttons_block}>
            <ExportUsers />
            {clientRegister && (
              <Button
                onClick={() => {
                  setIsClientRegisterOpen(true);
                }}
                iconName="CirclePlusIcon"
              >
                {t('client.create')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
